import {
  ControlField,
  Grouping,
  Section,
} from '../../../contexts/MetadataContext';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useFieldHidden } from '@brightmls/nextgen.lae.flc';
import { ListingResource, PropertyField } from '../../../system/field';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import MemoizedField, { MemoizedFieldContextProps } from './MemoizedField';
import { toSectionSlug } from '../../../utils/sectionSlug';

interface MemoizedSectionProps {
  groupings: Grouping[];
  section: Section;
  groupIndex: number;
  sectionIndex: number;
  totalSections: number;
  onCustomControlLoaded: (fieldKey: string) => void;
  sectionRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
  groupName: string;
  fieldContext?: MemoizedFieldContextProps; // Optional context override
  lockLayout?: boolean;
  fieldFramePrefix?: string;
  v2?: boolean;
}

function isSingleLayout(field: ControlField) {
  return (
    field.Layout.toLowerCase() === 'single' ||
    field.Layout.toLowerCase() === 'fluid-single' ||
    field.Control === 'MultiSelectField'
  );
}

const MemoizedSection: React.FC<MemoizedSectionProps> = React.memo(
  ({
    groupings,
    section,
    groupIndex,
    sectionIndex,
    totalSections,
    onCustomControlLoaded,
    sectionRefs,
    groupName,
    fieldContext,
    lockLayout,
    fieldFramePrefix,
    v2,
  }) => {
    // Use FLC's useFieldHidden hook when fieldContext is not provided
    const flcIsFieldHidden = useFieldHidden();
    const isFieldHidden = fieldContext
      ? fieldContext.isFieldHidden
      : flcIsFieldHidden;

    const handleCustomControlLoaded = useCallback(
      (fieldKey: string) => {
        onCustomControlLoaded(fieldKey);
      },
      [onCustomControlLoaded],
    );

    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (sectionRef.current) {
        sectionRefs.current[`${groupName}|${section.name}`] =
          sectionRef.current;
      }
    }, [sectionRefs, groupName, section.name]);

    const visibleFields = useMemo(() => {
      return section.fields.filter(
        (field) =>
          !isFieldHidden(ListingResource.Property, field.Key as PropertyField),
      );
    }, [section.fields, isFieldHidden]);

    const renderFields = useCallback(
      (fields: ControlField[]) => {
        const rows: ReactElement[] = [];
        let currentRow: { field: ReactElement; key: number }[] = [];
        let currentGrouping: string | undefined = undefined;
        let groupingContent: ReactElement[] = [];

        const pushCurrentRow = () => {
          if (currentRow.length > 0) {
            const key = currentRow.map((field) => field.key).join('_');
            const rowContent = (
              <Grid
                container
                spacing={3}
                key={`row-${rows.length}${currentGrouping ? `-${currentGrouping}` : ''}-${key}`}
                mb={4}
              >
                {currentRow.map((o) => o.field)}
              </Grid>
            );
            if (currentGrouping) {
              groupingContent.push(rowContent);
            } else {
              rows.push(rowContent);
            }
            currentRow = [];
          }
        };

        const pushCurrentGrouping = () => {
          if (groupingContent.length > 0) {
            const grouping = groupings.find((g) => g.Key === currentGrouping);
            rows.push(
              <React.Fragment
                key={`grouping-wrapper-${currentGrouping}-${groupIndex}-${sectionIndex}`}
              >
                {grouping && grouping.Title && (
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, fontWeight: 'bold' }}
                  >
                    {grouping.Title}
                  </Typography>
                )}
                <Box
                  key={`grouping-${currentGrouping}-${groupIndex}-${sectionIndex}`}
                  sx={{
                    ml: -2,
                    p: 2,
                    pb: '5px',
                    mb: 3,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                  }}
                >
                  {groupingContent}
                </Box>
              </React.Fragment>,
            );
            groupingContent = [];
          }
        };

        const renderField = (field: ControlField, index: number) => {
          if (lockLayout) {
            // Fixed width for lockLayout
            const width = isSingleLayout(field) ? '100%' : '50%';
            return (
              <Grid
                component="div"
                container
                sx={{
                  width: width,
                  maxWidth: width,
                  flexBasis: width,
                }}
                key={`${groupIndex}_${sectionIndex}_${field.Key}_${index}`}
              >
                <MemoizedField
                  field={field}
                  fieldContext={fieldContext}
                  onCustomControlLoaded={handleCustomControlLoaded}
                  fieldFramePrefix={fieldFramePrefix}
                />
              </Grid>
            );
          }

          if (field.HideFromMainForm) {
            return null;
          }

          // Regular responsive behavior for desktop
          return (
            <Grid
              size={{
                xs: 12,
                sm: 12,
                md: 12,
                lg: isSingleLayout(field) ? 12 : 6,
              }}
              key={`${groupIndex}_${sectionIndex}_${field.Key}_${index}`}
            >
              <MemoizedField
                field={field}
                fieldContext={fieldContext}
                onCustomControlLoaded={handleCustomControlLoaded}
              />
            </Grid>
          );
        };

        for (let i = 0; i < fields.length; i++) {
          const field = fields[i];
          if (!field) {
            continue;
          }

          const groupingKey =
            typeof field.GroupingKey === 'undefined' || field.GroupingKey === ''
              ? undefined
              : field.GroupingKey;

          if (groupingKey !== currentGrouping) {
            pushCurrentRow();
            pushCurrentGrouping();
            currentGrouping = field.GroupingKey || undefined;
          }

          if (field.Layout === 'paired') {
            pushCurrentRow(); // Ensure we start a new row
            currentRow.push({
              field: renderField(field, i),
              key: i,
            });
            if (i + 1 < fields.length) {
              currentRow.push({
                field: renderField(fields[i + 1], i + 1),
                key: i + 1,
              });

              i++; // Skip the next field as we've already rendered it
            }
            pushCurrentRow();
          } else if (isSingleLayout(field)) {
            pushCurrentRow();
            const singleFieldRow = (
              <Grid
                container
                spacing={3}
                key={`${groupIndex}_${sectionIndex}_row_${rows.length}-${i}`}
                mb={2}
              >
                {renderField(field, i)}
              </Grid>
            );
            if (currentGrouping) {
              groupingContent.push(singleFieldRow);
            } else {
              rows.push(singleFieldRow);
            }
          } else if (field.Layout === 'fluid') {
            currentRow.push({
              field: renderField(field, i),
              key: i,
            });
            // Only push the row if we have two fields or if the next field isn't fluid
            if (
              currentRow.length === 2 ||
              i === fields.length - 1 ||
              (fields[i + 1] && fields[i + 1].Layout !== 'fluid')
            ) {
              pushCurrentRow();
            }
          }
        }

        pushCurrentRow(); // Ensure any remaining fields are pushed
        pushCurrentGrouping();

        return rows;
      },
      [
        groupIndex,
        sectionIndex,
        groupings,
        handleCustomControlLoaded,
        fieldContext,
        lockLayout,
      ],
    );

    // If there are no visible fields, don't render anything
    if (visibleFields.length === 0) {
      return null;
    }

    return section.name ? (
      <Box
        ref={sectionRef}
        id={toSectionSlug(section.name)}
        data-scroll-target
        sx={{ mb: 4 }}
        key={`${groupIndex}_${sectionIndex}_section_container`}
      >
        <Box
          sx={{
            width: '100%',
            fontWeight: 'bold',
            fontSize: '1.2rem',
            my: 3,
            pl: 4,
          }}
          key={`${groupIndex}_${sectionIndex}_section_name`}
        >
          {section.name}
        </Box>
        <Box key={`${groupIndex}_${sectionIndex}_section_fields`}>
          {!v2 && (
            <Paper elevation={0} sx={{ pl: 4, pr: 4 }}>
              {renderFields(visibleFields)}
            </Paper>
          )}
          {v2 && <Box sx={{ pl: 4, pr: 4 }}>{renderFields(visibleFields)}</Box>}
        </Box>
        {!v2 && sectionIndex !== totalSections - 1 && (
          <Divider
            sx={{
              mx: 2,
            }}
          />
        )}
      </Box>
    ) : (
      <Box key={`${groupIndex}_${sectionIndex}_section_hidden`}>
        {renderFields(visibleFields)}
      </Box>
    );
  },
);

MemoizedSection.displayName = 'MemoizedSection';

export default MemoizedSection;
